import { CONTENT_GROUPS, PAGE_TYPES } from './constants'
import { PAGE_TYPE_OVERRIDES, getSlugPageType } from './page-type-overrides'

// SHA-256 via the browser's built-in Web Crypto API — avoids pulling in js-sha256.
// Returns '' when called outside the browser or when SubtleCrypto isn't available
// (older HTTP contexts). All hashEmail/hashPhone callers are gated on `process.client`
// in the GTM plugin and inside form success handlers, so SSR never hits this path.
async function sha256Hex(input) {
  if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle) return ''
  const data = new TextEncoder().encode(input)
  const hashBuf = await window.crypto.subtle.digest('SHA-256', data)
  const bytes = new Uint8Array(hashBuf)
  let hex = ''
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, '0')
  }
  return hex
}

// Mikro has no public login flow today; getUserProperty stays stubbed.
// Keep the field shape stable so a future auth integration only touches this file.
export function getUserProperty() {
  return {
    user_id: null,
    login_status: 'guest',
  }
}

export function determineContentGroups(path /* , pageProps */) {
  const pathSegments = (path || '').split('/').filter(Boolean)

  if (pathSegments.length === 0 || path === '/' || !path) {
    return {
      content_group: CONTENT_GROUPS.PAGE,
      content_group2: CONTENT_GROUPS.HOME_PAGE,
      content_group3: '',
      content_group4: '',
    }
  }

  const contentGroup =
    pathSegments.length === 1 ? CONTENT_GROUPS.PAGE : CONTENT_GROUPS.DETAIL_PAGE

  return {
    content_group: contentGroup,
    content_group2: pathSegments[0] || '',
    content_group3: pathSegments[1] || '',
    content_group4: pathSegments[2] || '',
  }
}

export function determineBlogContentGroups(blogTitle, blogCategory) {
  return {
    content_group: CONTENT_GROUPS.BLOG_PAGES,
    content_group2: CONTENT_GROUPS.BLOG,
    content_group3: (blogCategory && blogCategory.label) || CONTENT_GROUPS.NO_CATEGORY,
    content_group4: blogTitle || '',
  }
}

export async function hashEmail(email) {
  if (!email) return ''
  return sha256Hex(String(email).trim().toLowerCase())
}

export async function hashPhone(phone) {
  if (!phone) return ''
  const digits = String(phone).replace(/\D/g, '')
  return digits ? sha256Hex(digits) : ''
}

export function stripHtmlTags(html) {
  if (!html) return ''
  if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(String(html), 'text/html')
    return doc.body.textContent || ''
  }
  return String(html).replace(/<[^>]*>/g, '').trim()
}

// All Mikro pages live at the root (e.g. blog posts at `/amortisman-nedir/`,
// products at `/mikro-fly/`, nested CMS pages at `/hakkimizda/foo/`). The slug
// itself — not its first URL segment — determines page_type. SLUG_PAGE_TYPE_MAP
// is populated from breadcrumbs.json (which mirrors slugs.js) at app boot.
//
// Precedence:
//   1. explicit `override` argument
//   2. PAGE_TYPE_OVERRIDES[fullSlug | firstSegment]   (hand-curated, SEO-doc-named)
//   3. SLUG_PAGE_TYPE_MAP[fullSlug | firstSegment]    (built from breadcrumbs.json)
//   4. URL-derived backstop: `${firstSegment}_page` (only when slug is absent
//      from the snapshot — should be rare in production)
export function determinePageType(pathname, override) {
  if (override) return override
  if (!pathname || pathname === '/') return PAGE_TYPES.HOME_PAGE

  const fullSlug = pathname.replace(/^\/+|\/+$/g, '')
  if (!fullSlug) return PAGE_TYPES.HOME_PAGE

  const firstSegment = fullSlug.split('/')[0]

  if (PAGE_TYPE_OVERRIDES[fullSlug]) return PAGE_TYPE_OVERRIDES[fullSlug]
  if (PAGE_TYPE_OVERRIDES[firstSegment]) return PAGE_TYPE_OVERRIDES[firstSegment]

  const fullMapped = getSlugPageType(fullSlug)
  if (fullMapped) return fullMapped
  const segmentMapped = getSlugPageType(firstSegment)
  if (segmentMapped) return segmentMapped

  return `${firstSegment}${PAGE_TYPES.PAGE_SUFFIX}`
}

// Computes a reading-duration string like "6 dk" from a post body.
// Strips HTML, counts whitespace-separated tokens, divides by 200 wpm,
// rounds up, and adds " dk". Returns '' when no body is provided.
export function computeReadingDuration(body, wpm = 200) {
  if (!body) return ''
  const text = stripHtmlTags(body).trim()
  if (!text) return ''
  const words = text.split(/\s+/).filter(Boolean).length
  if (!words) return ''
  const minutes = Math.max(1, Math.ceil(words / wpm))
  return `${minutes} dk`
}
